import React, { useCallback, useEffect, useState } from "react";
import { Copy, CheckCircle, Clock, Upload, MessageCircle } from "lucide-react";
import api from "../api/api";
import toast from "react-hot-toast";
import { money, orderAccess, paymentLabel } from "../utils/directPayment";
import EmbedlyPayment from "./EmbedlyPayment";
import PaymentReceiptPreview from "./PaymentReceiptPreview";

const button =
  "rounded-xl px-4 py-3 text-sm font-semibold border border-[var(--store-border)] disabled:opacity-50 disabled:cursor-not-allowed";
function TransferCard({ payment, orderId, reload }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [progress, setProgress] = useState(0);
  const p = payment.manualPayment;
  const expired =
    payment.payment_status === "awaiting_transfer" &&
    new Date(p.expiresAt) <= new Date();
  const editable =
    !expired &&
    ["awaiting_transfer", "under_review", "needs_attention"].includes(
      payment.payment_status,
    );
  const whatsapp = `https://wa.me/${p.account.whatsappNumber}?text=${encodeURIComponent(`Hello ${p.shopName}, I would like to confirm my transfer of ${money(p.totalMinor)} for order ${p.reference}. Please check your bank account and confirm my payment.`)}`;
  const act = async (fn) => {
    setBusy(true);
    try {
      await fn();
      await reload();
    } catch (e) {
      toast.error(
        e.response?.data?.error ||
          "Unable to complete that action. Please retry.",
      );
    } finally {
      setBusy(false);
    }
  };
  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch (_) {
      toast.error("Please select and copy the number manually.");
    }
  };
  const download = async () => {
    const { data } = await api.get(`/payment/direct/receipts/${p.receiptId}`, {
      ...orderAccess(orderId),
      responseType: "blob",
    });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "payment-receipt";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  return (
    <article className="rounded-2xl border border-[var(--store-border)] bg-[var(--store-product-card-bg)] p-5 sm:p-4 space-y-5">
      <div className="flex justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-semibold text-lg">{p.shopName}</h2>
          <p className="text-sm opacity-70">Bank transfer to merchant</p>
        </div>
        <span className="text-sm flex items-center gap-2">
          {payment.payment_status === "paid" ? (
            <CheckCircle size={17} />
          ) : (
            <Clock size={17} />
          )}
          {expired
            ? "Payment deadline passed"
            : paymentLabel(payment.payment_status)}
        </span>
      </div>
      <div>
        <p className="text-sm opacity-70">
          {payment.payment_status === "paid"
            ? "Amount confirmed"
            : "Transfer amount"}
        </p>
        <p className="text-3xl font-bold mt-1">{money(p.totalMinor)}</p>
        <p className="text-xs opacity-70 mt-1">
          Items {money(p.goodsMinor)} · Delivery {money(p.shippingMinor)} · No
          transaction fee
        </p>
      </div>
      <dl className="rounded-xl bg-[var(--store-bg)] p-4 space-y-3">
        <div className="flex justify-between gap-3">
          <dt className="opacity-70">Bank</dt>
          <dd>{p.account.bankName}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="opacity-70">Account number</dt>
          <dd className="flex gap-3 items-center font-mono text-lg">
            {p.account.accountNumber}
            <button
              aria-label="Copy account number"
              onClick={() => copy(p.account.accountNumber)}
            >
              <Copy size={18} />
            </button>
          </dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="opacity-70">Account name</dt>
          <dd className="text-right">{p.account.accountName}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt className="opacity-70">Reference</dt>
          <dd className="flex gap-3 items-center font-mono text-sm">
            {p.reference}
            <button
              aria-label="Copy payment reference"
              onClick={() => copy(p.reference)}
            >
              <Copy size={16} />
            </button>
          </dd>
        </div>
      </dl>
      {p.account.instructions && (
        <p className="text-sm">{p.account.instructions}</p>
      )}
      {editable && (
        <p className="text-sm opacity-80">
          Transfer the exact amount and use this reference in your bank
          narration. The merchant confirms payment after checking their bank
          account.
        </p>
      )}
      {(expired || payment.payment_status === "cancelled") && (
        <p role="status" className="text-sm">
          This checkout is closed. If you already sent money, contact the
          merchant to arrange a new order or refund.
        </p>
      )}
      {payment.payment_status === "under_review" && (
        <p
          role="status"
          className="rounded-xl p-4 bg-amber-100 text-amber-950 text-sm"
        >
          Your transfer has been submitted. The merchant will confirm after
          checking their bank account. You do not need to pay again.
        </p>
      )}
      {payment.payment_status === "needs_attention" && (
        <p
          role="status"
          className="rounded-xl p-4 bg-amber-100 text-amber-950 text-sm"
        >
          <strong>Merchant needs clarification:</strong> {p.reason} Update your
          evidence or contact the merchant before making another transfer.
        </p>
      )}
      {payment.payment_status === "paid" && (
        <p className="text-sm">
          Confirmed by the merchant on{" "}
          {new Date(p.confirmedAt).toLocaleString()}.
        </p>
      )}
      {p.refundStatus !== "none" && (
        <p className="text-sm">
          {p.refundStatus === "required"
            ? "Order cancelled. The merchant needs to arrange your refund directly."
            : "The merchant has recorded your refund. Contact them if it has not arrived."}
        </p>
      )}
      {p.receiptId && (
        <>
          <PaymentReceiptPreview receiptId={p.receiptId} orderId={orderId} />
          <button
            className={button}
            disabled={busy}
            onClick={() => act(download)}
          >
            Download uploaded receipt
          </button>
        </>
      )}
      <div className="flex flex-wrap gap-3">
        {editable && (
          <label className={`${button} cursor-pointer flex items-center gap-2`}>
            <Upload size={17} />
            {busy && progress > 0
              ? `Uploading ${progress}%`
              : p.receiptId
                ? "Upload another receipt"
                : "Upload payment receipt"}
            <input
              className="sr-only"
              type="file"
              aria-label="Payment receipt"
              accept="image/jpeg,image/png,application/pdf"
              disabled={busy}
              onChange={(e) => {
                const file = e.target.files[0];
                e.target.value = "";
                if (!file) return;
                if (
                  file.size > 5 * 1024 * 1024 ||
                  !["image/jpeg", "image/png", "application/pdf"].includes(
                    file.type,
                  )
                )
                  return toast.error("Choose a JPEG, PNG or PDF up to 5 MB.");
                act(async () => {
                  const body = new FormData();
                  body.append("receipt", file);
                  setProgress(1);
                  try {
                    await api.post(
                      `/payment/direct/${payment._id}/receipt`,
                      body,
                      {
                        ...orderAccess(orderId),
                        onUploadProgress: (e) =>
                          setProgress(
                            Math.round(
                              (e.loaded * 100) / (e.total || file.size),
                            ),
                          ),
                      },
                    );
                    toast.success(
                      "Receipt uploaded. Submit your transfer for review when ready.",
                    );
                  } finally {
                    setProgress(0);
                  }
                });
              }}
            />
          </label>
        )}
        {p.account.whatsappNumber ? (
          <a
            className={`${button} flex items-center gap-2`}
            href={whatsapp}
            target="_blank"
            rel="noopener noreferrer"
          >
            <MessageCircle size={17} />
            Contact merchant on WhatsApp
          </a>
        ) : (
          editable && (
            <p className="text-sm opacity-80">
              Submit your transfer below for merchant confirmation. You can
              attach a receipt to help them match your payment.
            </p>
          )
        )}
      </div>
      {editable && (
        <p className="text-xs opacity-70">
          Receipt optional · JPEG, PNG or PDF · Up to 5 MB
        </p>
      )}
      {editable && payment.payment_status !== "under_review" && (
        <div className="space-y-3">
          <label className="block text-sm">
            Sender name or transfer note (optional)
            <textarea
              maxLength={1000}
              className="block w-full mt-2 p-3 rounded-xl border border-[var(--store-border)] bg-[var(--store-bg)]"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          <button
            className={`${button} w-full bg-[var(--store-button-bg)] text-[var(--store-button-text)]`}
            disabled={busy}
            onClick={() =>
              act(() =>
                api.post(
                  `/payment/direct/${payment._id}/submit`,
                  { note },
                  orderAccess(orderId),
                ),
              )
            }
          >
            I've made the transfer
          </button>
        </div>
      )}
      {payment.payment_status === "awaiting_transfer" && !expired && (
        <p className="text-xs opacity-70">
          Complete and report your transfer before{" "}
          {new Date(p.expiresAt).toLocaleString()} to keep this order reserved.
        </p>
      )}
      <details className="text-sm">
        <summary className="cursor-pointer">Payment history</summary>
        <ol className="mt-3 space-y-2">
          {p.history.map((item, i) => (
            <li key={i}>
              {new Date(item.at).toLocaleString()} ·{" "}
              {item.action.replace(/_/g, " ")}
              {item.note && ` — ${item.note}`}
            </li>
          ))}
        </ol>
      </details>
    </article>
  );
}

export default function DirectTransferCheckout({ orderId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [email, setEmail] = useState("");
  const [recoveryId, setRecoveryId] = useState(orderId || "");
  const [recovery, setRecovery] = useState("");
  const load = useCallback(async () => {
    if (!orderId) {
      setError(
        "Open a payment from your order history, or recover a guest order below.",
      );
      return;
    }
    try {
      const response = await api.get(
        `/payment/direct/orders/${orderId}`,
        orderAccess(orderId),
      );
      setData(response.data);
      setError("");
    } catch (e) {
      setError(
        e.response?.data?.error || "Could not load your payment. Please retry.",
      );
    }
  }, [orderId]);
  useEffect(() => {
    load();
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [load]);
  if (!data)
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold">Your payment</h1>
        {!error ? (
          <p>Loading payment details…</p>
        ) : (
          <>
            <p role="alert">{error}</p>
            <button className={button} onClick={load}>
              Retry
            </button>
            <form
              className="space-y-3"
              onSubmit={async (e) => {
                e.preventDefault();
                try {
                  const { data } = await api.post("/payment/direct/recover", {
                    orderId: recoveryId,
                    email,
                  });
                  setRecovery(data.message);
                } catch (_) {
                  setRecovery(
                    "Could not request your link. Please try again later.",
                  );
                }
              }}
            >
              <h2 className="font-semibold">Recover a guest order</h2>
              <label className="block">
                Order ID
                <input
                  required
                  className="block border rounded p-2 bg-transparent w-full"
                  value={recoveryId}
                  onChange={(e) => setRecoveryId(e.target.value)}
                />
              </label>
              <label className="block">
                Checkout email
                <input
                  required
                  type="email"
                  className="block border rounded p-2 bg-transparent w-full"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </label>
              <button className={button}>Email my private link</button>
              <p role="status">{recovery}</p>
            </form>
          </>
        )}
      </div>
    );
  if (data.order.payment_method !== "manual_bank_transfer")
    return (
      <>
        <h1 className="text-2xl font-semibold">Order payment</h1>
        {data.order.payment_status === "paid" ? (
          <p>Payment confirmed.</p>
        ) : (
          <EmbedlyPayment
            orderId={orderId}
            price={data.order.price + (data.order.shipping_fee || 0)}
            customerName={data.order.shippingInfo.name}
            fromStore={data.order.storeSlug}
          />
        )}
      </>
    );
  const paid = data.payments.filter((p) => p.payment_status === "paid").length;
  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm opacity-70">Order #{orderId}</p>
        <h1 className="text-3xl font-semibold mt-2">
          {paid === data.payments.length
            ? "Payment confirmed"
            : "Pay by bank transfer"}
        </h1>
        <p className="mt-2 opacity-80">
          {data.payments.length > 1
            ? "Make a separate transfer to each merchant below."
            : "Pay directly to the merchant’s bank account."}
        </p>
        <p className="mt-3 font-medium">
          {paid} of {data.payments.length} merchant payments confirmed
        </p>
        <p className="text-sm mt-1">
          Order total:{" "}
          {money(
            Math.round((data.order.price + data.order.shipping_fee) * 100),
          )}
        </p>
      </div>
      {error && <p role="alert">{error}</p>}
      {data.payments.map((payment) => (
        <TransferCard
          key={payment._id}
          payment={payment}
          orderId={orderId}
          reload={load}
        />
      ))}
      <p className="text-xs opacity-70">
        Keep this page to check payment updates. Guest customers can also return
        through their private email link.
      </p>
    </div>
  );
}
